import convert from "convert-units";
import round from "lodash/round";
import { PARAMETER_OPTIONS } from "../../../../../../constants";
import PRBadge from "../../../../../../parts/PRBadge";
import StatusIcon from "../../../../../../parts/StatusIcon";
import { expandSetOutcomes } from "../../../../../../utils/actuals";
import { getComplexSetOutcomeReps } from "../../../../../../utils/complex";
import { isParameterBlank } from "../../../../../../utils/exercise-validations";
import { getLoadDrop } from "../../../../../../utils/load-drop";
import { repsValue } from "../../../../../../utils/reps-range";
import { rpeValue } from "../../../../../../utils/rpe-range";
import { ActionsButtons } from "./ActionsButtons";
import RpeInput from "../../../../../../parts/inputs/RpeInput";

const { SETS, REPS, RPEAMRAP, LOAD } = PARAMETER_OPTIONS;

const TableRow = ({
  exerciseId,
  set,
  handler,
  idx,
  hasSets = false,
  exerciseHasCustomRPE = false,
  primary = "squat",
  editDayMode = false,
  editable,
  hasActuals = false,
  hasPR = false,
  prByRound = {},
  exercise,
  setNumberOffset = 0,
  repMaximums,
  addTempSet,
  removeSet,
  removeTempSet,
  withWeight,
  fromMetric,
  isExerciseStarter = false,
  intensityMeasure,
  isRiR,
  units,
  hasLoadDrop,
  complexTwo,
  complexThree,
  complexTwoSet,
  complexThreeSet,
}) => {
  const { sets, reps, rpe, load, percentRepMax, repsLower, loadDrop } = set;
  const isTemp = set.temp === true;
  const loadToShow = load > 0 ? fromMetric(load) : "";
  // getting the current idx since idx is not really an index but an `id-idx`
  const rowPosition = Number(idx.split("-")[1]);
  if (editDayMode && editable) {
    const exerciseRepMax = exerciseHasCustomRPE || repMaximums[primary];

    return (
      <tr key={idx}>
        <td
          className="sets"
          data-empty={isParameterBlank(set, SETS) || undefined}
        >
          <input
            className="text-input"
            id={`sets-${exerciseId}-${idx}`}
            value={sets}
            onChange={(evt) =>
              handler({
                value: evt.target.value,
                field: "sets",
                exerciseId,
                setIdx: idx,
              })
            }
          />
        </td>
        <td data-empty={isParameterBlank(set, REPS) || undefined}>
          <div
            className={`flex gap-1 items-center${
              complexTwoSet ? " min-w-24" : ""
            }`}
          >
            <input
              id={`reps-${exerciseId}-${idx}`}
              className="text-input"
              value={reps}
              onChange={(evt) =>
                handler({
                  value: evt.target.value,
                  field: "reps",
                  exerciseId,
                  setIdx: idx,
                })
              }
            />
            {complexTwoSet && (
              <input
                id={`reps-${complexTwo?._id}-${idx}`}
                value={complexTwoSet?.reps}
                onChange={(evt) =>
                  handler({
                    value: evt.target.value,
                    field: "reps",
                    exerciseId: complexTwo._id,
                    setIdx: idx,
                  })
                }
              />
            )}
            {complexThreeSet && (
              <input
                id={`reps-${complexThree?._id}-${idx}`}
                value={complexThreeSet?.reps}
                onChange={(evt) =>
                  handler({
                    value: evt.target.value,
                    field: "reps",
                    exerciseId: complexThree._id,
                    setIdx: idx,
                  })
                }
              />
            )}
          </div>
        </td>
        <td
          className="rpe-rir"
          data-empty={isParameterBlank(set, RPEAMRAP) || undefined}
        >
          <RpeInput
            id={`rpe-${exerciseId}-${idx}`}
            className="text-input"
            name="rpe"
            value={rpe ?? ""}
            isRiR={isRiR}
            placeholder={intensityMeasure}
            onChange={(evt) =>
              handler({
                value: evt.target.value,
                field: "rpe",
                exerciseId,
                setIdx: idx,
              })
            }
          />
        </td>
        <td
          className="td-load"
          colSpan={hasLoadDrop ? 2 : 1}
          data-empty={isParameterBlank(set, LOAD) || undefined}
        >
          {hasLoadDrop ? (
            <input
              className="text-left!"
              value={loadDrop}
              onChange={(evt) => {
                evt.preventDefault();
                if (String(evt.target.value).includes(",")) {
                  evt.target.value = String(evt.target.value).replace(",", ".");
                }
                handler({
                  value: evt.target.value,
                  field: "loadDrop",
                  exerciseId,
                  setIdx: idx,
                });
              }}
            />
          ) : (
            <input
              id={`load-${exerciseId}-${idx}`}
              className="text-input"
              defaultValue={loadToShow}
              onChange={(evt) => {
                evt.preventDefault();
                if (String(evt.target.value).includes(",")) {
                  evt.target.value = String(evt.target.value).replace(",", ".");
                }
                if (Number.isNaN(evt.target.value)) return;
                let value = evt.target.value;
                if (units === "imperial" && value !== "") {
                  value = round(convert(value).from("lb").to("kg"), 2);
                }
                handler({
                  value,
                  field: "load",
                  otherNew: {
                    field: "percentRepMax",
                    value:
                      value === ""
                        ? ""
                        : ((parseFloat(value) / exerciseRepMax) * 100).toFixed(
                            2
                          ),
                  },
                  exerciseId,
                  setIdx: idx,
                });
              }}
            />
          )}
          <span className="units-label">
            {hasLoadDrop ? "% mod." : units === "imperial" ? "lb" : "kg"}
          </span>
        </td>
        {isExerciseStarter || hasLoadDrop ? null : (
          <td className="intensity td-intensity">
            <input
              id={`percentRepMax-${exerciseId}-${idx}`}
              className="text-input"
              value={
                String(percentRepMax).includes(".") &&
                String(percentRepMax).split(".")[1].length > 1
                  ? Math.round(percentRepMax * 10) / 10
                  : percentRepMax
              }
              onChange={(evt) => {
                evt.preventDefault();
                if (String(evt.target.value).includes(",")) {
                  evt.target.value = String(evt.target.value).replace(",", ".");
                }
                if (Number.isNaN(evt.target.value)) return;
                handler({
                  value: evt.target.value,
                  field: "percentRepMax",
                  otherNew: {
                    field: "load",
                    value:
                      evt.target.value === ""
                        ? ""
                        : (
                            (parseFloat(evt.target.value) / 100) *
                            exerciseRepMax
                          ).toFixed(2),
                  },
                  exerciseId,
                  setIdx: idx,
                });
              }}
            />
            <span className="percentage-label">%</span>
          </td>
        )}

        <ActionsButtons
          extraCondition={hasSets}
          isTemp={isTemp}
          onAddSet={() => addTempSet({ exerciseId, pos: idx.split("-")[1] })}
          addSetImgSize={24}
          onRemoveSet={() => removeSet(exerciseId, idx.split("-")[1])}
          removeSetImgSize={18}
          onDiscardSet={() => removeTempSet(exerciseId, rowPosition)}
        />
      </tr>
    );
  }
  // Finished view: the athlete logged this exercise, so show what they actually
  // did rather than the plan. One row per performed round, numbered 1,2,3…
  // within its own set-group (setNumberOffset resets per target), so the
  // Sets column reads as a set number here rather than a count. Rounds with no
  // outcome fall back to the prescribed values and render as pending.
  if (hasActuals) {
    // The exercise's own snapshot first: the athlete's current 1RM may have
    // moved since, and %1RM should reflect the day it was performed.
    const exerciseRepMax =
      exerciseHasCustomRPE ||
      exercise?.repMaximums?.[primary] ||
      repMaximums?.[primary];

    return (
      <>
        {expandSetOutcomes(set).map(({ index, entry }) => {
          const { outcome, status } = entry || {};
          const isLogged = !!(entry && status);
          const source = isLogged ? outcome : set;
          const rowLoad = source.load;

          // Complex followers: the performed reps of the 2nd/3rd movement for
          // this round, falling back to their prescribed reps when unlogged.
          const { repsTwo, repsThree } = isLogged
            ? getComplexSetOutcomeReps(exercise || {}, rowPosition, index)
            : {
                repsTwo: complexTwo?.sets?.[rowPosition]?.reps,
                repsThree: complexThree?.sets?.[rowPosition]?.reps,
              };

          const repsCell = [
            isLogged ? source.reps : repsValue(source.reps, source.repsLower),
            repsTwo,
            repsThree,
          ]
            .filter((value) => value != null && value !== "")
            .join("+");

          const prReps = prByRound[`${rowPosition}-${index}`];

          const intensityCell = isLogged
            ? rowLoad > 0 && exerciseRepMax > 0
              ? `${round((rowLoad / exerciseRepMax) * 100, 1)}%`
              : "-"
            : percentRepMax > 0
              ? `${round(percentRepMax, 1)}%`
              : "-";

          return (
            <tr
              key={`${idx}-${index}`}
              className={
                status === "completed"
                  ? "ok"
                  : status === "missed"
                    ? "missed"
                    : "pending"
              }
            >
              <td className="sets">
                <span className="wk-status-inline">
                  <StatusIcon status={status} />
                </span>
                {setNumberOffset + index + 1}
              </td>
              <td>{repsCell || "-"}</td>
              {/* A logged set commonly carries rpe: 0, and convertToRiR passes
                  falsy values straight through — guard so the cell reads "-"
                  rather than a bogus 0. */}
              <td>
                {source.rpe
                  ? rpeValue(source.rpe, source.rpeLower, isRiR)
                  : "-"}
              </td>
              <td>
                {!isLogged && loadDrop
                  ? getLoadDrop(loadDrop)
                  : rowLoad > 0
                    ? `${withWeight(fromMetric(rowLoad))}`
                    : "-"}
              </td>
              {isExerciseStarter ? null : (
                <td className="intensity">{intensityCell}</td>
              )}
              {/* Rendered empty on non-PR rows so the columns stay aligned. */}
              {hasPR ? (
                <td className="pr-badge-col">
                  {prReps != null && <PRBadge reps={prReps} />}
                </td>
              ) : null}
            </tr>
          );
        })}
      </>
    );
  }

  return (
    <tr key={idx}>
      <td
        className="sets"
        data-empty={isParameterBlank(set, SETS) || undefined}
      >
        {sets}
      </td>
      <td data-empty={isParameterBlank(set, REPS) || undefined}>
        {repsValue(reps, repsLower)}
        {complexTwo?.sets?.[rowPosition]?.reps &&
          `+${complexTwo.sets[rowPosition].reps}`}
        {complexThree?.sets?.[rowPosition]?.reps &&
          `+${complexThree.sets[rowPosition].reps}`}
      </td>
      <td data-empty={isParameterBlank(set, RPEAMRAP) || undefined}>
        {rpeValue(rpe, set.rpeLower, isRiR)}
      </td>
      <td data-empty={isParameterBlank(set, LOAD) || undefined}>
        {loadDrop
          ? getLoadDrop(loadDrop)
          : load > 0
            ? `${withWeight(fromMetric(load))}`
            : "-"}
      </td>
      {isExerciseStarter ? null : (
        <td className="intensity">
          {percentRepMax > 0 ? `${round(percentRepMax, 1)}%` : "-"}
        </td>
      )}
    </tr>
  );
};

export default TableRow;
